import {
  BoardMoveIcon,
  CircleHelpIcon,
  FilePenLineIcon,
  FormIcon,
  LayersIcon,
  SummaryIcon,
  TagsIcon,
} from '@air/design-icons';

import { type WorkspaceAgentIconComponent } from '~/components/v2/components/workspace/workspace-agent-panel/design-system/types';
import { type WorkspaceAgentStructuredDisplay } from '~/components/v2/components/workspace/workspace-agent-panel/types/workspaceAgentStructuredDisplay';
import { type AttachedUploadBatch } from '~/components/v2/components/workspace/workspace-agent-panel/uploaded-batch-offer/useUploadedBatchOfferStore';

export type WorkspaceAgentPanelZeroStateSuggestion = {
  description: string;
  icon: WorkspaceAgentIconComponent;
  iconName: string;
  id: string;
  prompt: string;
  title: string;
};

/** What a zero-state chip draws; a chip that seeds a prompt is a full suggestion. */
export type WorkspaceAgentPanelZeroStateChip = Pick<WorkspaceAgentPanelZeroStateSuggestion, 'icon' | 'id' | 'title'>;

/** Zero state only: opens the Actions menu instead of seeding a prompt, so it has none. */
export const WORKSPACE_AGENT_PANEL_ACTIONS_MENU_CHIP = {
  icon: CircleHelpIcon,
  id: 'zero-state-actions-menu',
  title: 'See what else Air can do',
} satisfies WorkspaceAgentPanelZeroStateChip;

export const WORKSPACE_AGENT_PANEL_ZERO_STATE_SUGGESTIONS = [
  {
    id: 'hero-summarize-status',
    title: 'Get status on reviews & approvals',
    description: 'Review & approval status, unresolved comments, and blockers',
    iconName: 'summary',
    prompt:
      'Give me a review status update on the selected assets. For each, tell me where it stands in the review or approval process, surface any unresolved comments or feedback that need a response, and flag anything blocking it from being finished.',
    icon: SummaryIcon,
  },
  {
    id: 'hero-bulk-rename',
    title: 'Rename files across all assets',
    description: 'Prefix, suffix, or sequential numbering with preview',
    iconName: 'file-pen-line',
    prompt:
      'Rename the selected assets using a naming convention I provide. Show me a before and after before you execute the task.',
    icon: FilePenLineIcon,
  },
  {
    id: 'hero-bulk-tag-metadata',
    title: 'Bulk edit tags & custom fields on assets',
    description: 'Add, remove, or update tags and custom fields in bulk',
    iconName: 'tags',
    prompt:
      "Add tags and/or custom fields for the selected assets. Ask me to define what fields and values I'd like to apply and give me the option to get suggestions if I don't know what I want to do yet.",
    icon: TagsIcon,
  },
  {
    id: 'hero-artist-variants',
    title: 'Generate variants for social media',
    description: 'Crops, formats & watermark preview',
    iconName: 'layers',
    prompt: "Generate variants from the selected original assets. Ask me what types of basic edits I'd like to make.",
    icon: LayersIcon,
  },
] satisfies readonly WorkspaceAgentPanelZeroStateSuggestion[];

/** The default empty thread: the prompt suggestions, then the Actions menu opener. */
export const WORKSPACE_AGENT_PANEL_ZERO_STATE_CHIPS = [
  ...WORKSPACE_AGENT_PANEL_ZERO_STATE_SUGGESTIONS,
  WORKSPACE_AGENT_PANEL_ACTIONS_MENU_CHIP,
] satisfies readonly WorkspaceAgentPanelZeroStateChip[];

/** Selection/context menu only — not shown in composer zero-state chips. */
export const WORKSPACE_AGENT_HERO_EDIT_ALL_FIELDS = {
  id: 'hero-edit-all-fields',
  title: 'Edit all fields',
  description: 'Update custom fields across your selection',
  iconName: 'form',
  prompt:
    "Add custom fields for the selected assets. Ask me to define what fields and values I'd like to apply and give me the option to get suggestions if I don't know what I want to do yet.",
  icon: FormIcon,
} satisfies WorkspaceAgentPanelZeroStateSuggestion;

/** Selection/context menu only — not shown in composer zero-state chips. */
export const WORKSPACE_AGENT_HERO_RENAME_FILES_BULK = {
  id: 'hero-rename-files-bulk',
  title: 'Rename files in bulk',
  description: 'Rename multiple files in your selection',
  iconName: 'file-pen-line',
  prompt:
    'Rename the selected assets using a naming convention I provide. Show me a before and after before you execute the task.',
  icon: FilePenLineIcon,
} satisfies WorkspaceAgentPanelZeroStateSuggestion;

/** Edit tags modal promo only — not shown in composer zero-state chips. */
export const WORKSPACE_AGENT_HERO_SUGGEST_TAGS = {
  id: 'hero-suggest-tags',
  title: 'Suggest tags',
  description: 'Analyze content and propose tags for your selection',
  iconName: 'tags',
  prompt:
    'Suggest tags for the selected assets based on their content. When the selection is small, analyze each asset; when it is large, do not try to analyze every asset — sample or group assets, focus on themes that fit the selection, and say what you did not review. Lead with a scannable list of recommended tags. Ask me to confirm, adjust, narrow the selection, or apply — do not change tags until I approve.',
  icon: TagsIcon,
} satisfies WorkspaceAgentPanelZeroStateSuggestion;

/** Empty thread after the upload pill attaches a batch. Not shown in the default zero state. */
export const UPLOADED_BATCH_SUGGESTIONS = [
  {
    id: 'uploaded-batch-rename',
    title: 'Rename these files',
    description: 'Prefix, suffix, or sequential numbering with preview',
    iconName: 'file-pen-line',
    prompt:
      'Rename the selected assets using a naming convention I provide. Show me a before and after before you execute the task.',
    icon: FilePenLineIcon,
  },
  {
    id: 'uploaded-batch-tag',
    title: 'Add tags to these files',
    description: 'Add, remove, or update tags and custom fields in bulk',
    iconName: 'tags',
    prompt:
      "Add tags and/or custom fields for the selected assets. Ask me to define what fields and values I'd like to apply and give me the option to get suggestions if I don't know what I want to do yet.",
    icon: TagsIcon,
  },
  {
    id: 'uploaded-batch-move',
    title: 'Move these files to a board',
    description: 'Ask which board, then move after you confirm',
    iconName: 'board-move',
    prompt:
      'Move the selected assets to a board I choose. Ask me which board they should go to, and do not move them until I confirm.',
    icon: BoardMoveIcon,
  },
  {
    id: 'uploaded-batch-variants',
    title: 'Generate variants for social',
    description: 'Crops, formats & watermark preview',
    iconName: 'layers',
    prompt: "Generate variants from the selected original assets. Ask me what types of basic edits I'd like to make.",
    icon: LayersIcon,
  },
] satisfies readonly WorkspaceAgentPanelZeroStateSuggestion[];

const WORKSPACE_AGENT_PANEL_ALL_HERO_SUGGESTIONS = [
  ...WORKSPACE_AGENT_PANEL_ZERO_STATE_SUGGESTIONS,
  WORKSPACE_AGENT_HERO_EDIT_ALL_FIELDS,
  WORKSPACE_AGENT_HERO_RENAME_FILES_BULK,
  WORKSPACE_AGENT_HERO_SUGGEST_TAGS,
  ...UPLOADED_BATCH_SUGGESTIONS,
] satisfies readonly WorkspaceAgentPanelZeroStateSuggestion[];

const UPLOADED_BATCH_INTRO_SINGULAR =
  '1 file just landed from your upload. Air can rename it, tag it, move it, or generate variants.';

export const getUploadedBatchIntro = (fileCount: number) => {
  if (fileCount === 1) {
    return UPLOADED_BATCH_INTRO_SINGULAR;
  }

  return `${fileCount.toLocaleString()} files just landed from your upload. Air can rename them, tag them, move them, or generate variants.`;
};

const IMPORTED_BATCH_INTRO_SINGULAR =
  '1 file just landed from your import. Air can rename it, tag it, move it, or generate variants.';

const getImportedBatchIntro = (fileCount: number) => {
  if (fileCount === 1) {
    return IMPORTED_BATCH_INTRO_SINGULAR;
  }

  return `${fileCount.toLocaleString()} files just landed from your import. Air can rename them, tag them, move them, or generate variants.`;
};

export const getAttachedBatchIntro = ({ fileCount, source }: AttachedUploadBatch) =>
  source === 'import' ? getImportedBatchIntro(fileCount) : getUploadedBatchIntro(fileCount);

/** Prototype `TASK_INTRO` — used when the user has assets in scope. */
export const WORKSPACE_AGENT_PANEL_ZERO_STATE_INTRO_WITH_SELECTION =
  'Pick a task or describe something new — Air can get status, rename, tag, or generate variants from your selection.';

/** Zero-state nudge when nothing is selected — same hero capabilities, selection-first. */
export const WORKSPACE_AGENT_PANEL_ZERO_STATE_INTRO_NO_SELECTION =
  'Select some assets — then pick a task or describe something new. Air can get status, rename, tag, or generate variants.';

export const getWorkspaceAgentPanelZeroStateIntro = (hasSelection: boolean) =>
  hasSelection
    ? WORKSPACE_AGENT_PANEL_ZERO_STATE_INTRO_WITH_SELECTION
    : WORKSPACE_AGENT_PANEL_ZERO_STATE_INTRO_NO_SELECTION;

export const getWorkspaceAgentPanelZeroStateSuggestionById = (
  id: string,
): WorkspaceAgentPanelZeroStateSuggestion | undefined =>
  WORKSPACE_AGENT_PANEL_ALL_HERO_SUGGESTIONS.find((suggestion) => suggestion.id === id);

export const getWorkspaceAgentHeroStructuredDisplay = (id: string): WorkspaceAgentStructuredDisplay | undefined => {
  const suggestion = getWorkspaceAgentPanelZeroStateSuggestionById(id);

  if (!suggestion) {
    return undefined;
  }

  return {
    description: suggestion.description,
    icon: suggestion.iconName,
    title: suggestion.title,
  };
};
